import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Thermometer, Lock, Paperclip, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PageHeader, StatusBadge, Empty, Panel } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, fmtDateTime, todayIso, fileBlobUrl } from "@/lib/api";
import { can } from "@/lib/perm";

const ACCEPT = "application/pdf,image/jpeg,image/png,image/webp";

async function uploadMedical(sid, file) {
  if (file.size > 10 * 1024 * 1024) throw new Error("File troppo grande (max 10 MB)");
  const fd = new FormData();
  fd.append("file", file);
  await api.post(`/sick/${sid}/attachments`, fd);
}

function Attachments({ r, canUpload, onDone }) {
  const ref = useRef(null);
  const open = async (a) => {
    const w = window.open("", "_blank");
    try {
      const url = await fileBlobUrl(a.id);
      if (w) w.location.href = url;
    } catch (e) {
      w?.close();
      toast.error(errMsg(e));
    }
  };
  const add = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try { await uploadMedical(r.id, file); toast.success("Allegato caricato"); onDone(); } catch (e2) { toast.error(e2.response ? errMsg(e2) : e2.message); }
  };
  return (
    <div className="flex flex-col items-start gap-1">
      {(r.attachments || []).map((a) => (
        <button key={a.id} onClick={() => open(a)} className="inline-flex max-w-[180px] items-center gap-1 truncate text-xs text-primary hover:underline" data-testid={`sick-attachment-${a.id}`}>
          <FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{a.filename}</span>
        </button>
      ))}
      {canUpload && (
        <>
          <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={add} data-testid={`sick-add-file-input-${r.id}`} />
          <button onClick={() => ref.current?.click()} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid={`sick-add-attachment-${r.id}`}>
            <Paperclip className="h-3.5 w-3.5" />Allega
          </button>
        </>
      )}
      {!canUpload && !(r.attachments || []).length && <span className="text-xs text-muted-foreground">—</span>}
    </div>
  );
}

function NewSickDialog({ open, onOpenChange, onDone }) {
  const t = todayIso();
  const [f, setF] = useState({ start_date: t, end_date: t, protocol_number: "", note: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    if (file && file.size > 10 * 1024 * 1024) return toast.error("File troppo grande (max 10 MB)");
    setBusy(true);
    try {
      const { data } = await api.post("/sick", f);
      if (file) await uploadMedical(data.id, file);
      toast.success("Malattia comunicata");
      onOpenChange(false);
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
      onDone();
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="new-sick-dialog">
        <DialogHeader>
          <DialogTitle>Comunica malattia</DialogTitle>
          <DialogDescription>Indica il numero di protocollo del certificato medico telematico.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={f.start_date} onChange={set("start_date")} required data-testid="sick-start-date-input" /></div>
            <div className="space-y-1.5"><Label>Al</Label><Input type="date" value={f.end_date} min={f.start_date} onChange={set("end_date")} required data-testid="sick-end-date-input" /></div>
          </div>
          <div className="space-y-1.5"><Label>Numero di protocollo</Label><Input value={f.protocol_number} onChange={set("protocol_number")} required minLength={3} data-testid="sick-protocol-input" /></div>
          <div className="space-y-1.5"><Label>Note</Label><Textarea value={f.note} onChange={set("note")} rows={3} data-testid="sick-note-input" /></div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Allegato medico (opzionale, PDF/JPG/PNG, max 10 MB)</Label>
            <Input type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="sick-file-input" />
            <p className="text-xs text-muted-foreground">Visibile solo a te e agli amministratori. Ogni accesso viene registrato.</p>
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="sick-submit-button">Invia</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AckDialog({ item, onClose, onDone }) {
  const [note, setNote] = useState("");
  const ack = async () => {
    try {
      await api.post(`/sick/${item.id}/acknowledge`, { admin_note: note });
      toast.success("Presa visione registrata");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="ack-dialog">
        <DialogHeader>
          <DialogTitle>Presa visione</DialogTitle>
          <DialogDescription>{item?.user_name} · Prot. {item?.protocol_number}</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" />Nota riservata (visibile solo agli amministratori)</Label>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="ack-note-input" />
        </div>
        <DialogFooter><Button onClick={ack} data-testid="ack-confirm-button">Conferma presa visione</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Malattie() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const canSick = can(user, "view_sick");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [ack, setAck] = useState(null);
  const load = useCallback(() => api.get("/sick", { params: { scope: canSick ? "all" : "mine" } }).then((r) => setItems(r.data)), [canSick]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="sick-page">
      <PageHeader title="Malattie" subtitle={canSick ? "Segnalazioni di malattia del personale. Si registra la presa visione, non l'approvazione." : "Comunica i giorni di malattia con il numero di protocollo."}
        actions={<Button onClick={() => setOpen(true)} data-testid="new-sick-button"><Plus className="mr-1.5 h-4 w-4" />{isAdmin ? "Registra la mia malattia" : "Comunica malattia"}</Button>} />
      {items.length === 0 ? (
        <Empty icon={Thermometer} text="Nessuna segnalazione di malattia." testId="sick-empty" />
      ) : (
        <Panel className="overflow-x-auto fade-up">
          <Table data-testid="sick-table">
            <TableHeader>
              <TableRow>
                {canSick && <TableHead>Dipendente</TableHead>}
                <TableHead>Periodo</TableHead>
                <TableHead>Giorni</TableHead>
                <TableHead>Protocollo</TableHead>
                <TableHead>Allegati</TableHead>
                <TableHead>Stato</TableHead>
                {canSick && <TableHead>Nota riservata</TableHead>}
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} data-testid={`sick-row-${r.id}`}>
                  {canSick && <TableCell className="font-medium">{r.user_name}</TableCell>}
                  <TableCell className="whitespace-nowrap">{fmtDate(r.start_date)} → {fmtDate(r.end_date)}</TableCell>
                  <TableCell>{r.days}</TableCell>
                  <TableCell className="font-mono text-xs">{r.protocol_number}</TableCell>
                  <TableCell><Attachments r={r} canUpload={r.user_id === user.id && (r.attachments || []).length < 5} onDone={load} /></TableCell>
                  <TableCell>
                    <StatusBadge status={r.status} testId={`sick-status-${r.id}`} />
                    {r.acknowledged_at && <span className="mt-1 block text-xs text-muted-foreground">{r.acknowledged_by} · {fmtDateTime(r.acknowledged_at)}</span>}
                  </TableCell>
                  {canSick && <TableCell className="max-w-xs text-sm text-muted-foreground">{r.admin_note || "—"}</TableCell>}
                  <TableCell className="text-right">
                    {canSick && r.user_id !== user.id && r.status === "submitted" && <Button size="sm" onClick={() => setAck(r)} data-testid={`sick-ack-${r.id}`}>Prendi visione</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Panel>
      )}
      {open && <NewSickDialog open={open} onOpenChange={setOpen} onDone={load} />}
      <AckDialog item={ack} onClose={() => setAck(null)} onDone={load} />
    </div>
  );
}
